import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { topRightToast } from "../Shadcn/Sonner";
import { useMap, Map, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { useMapsService } from "../../services";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useQuery } from "@tanstack/react-query";
import { brandList, cifTypeState, choroplethModeState, errorState, filterDefaultState, filterLoadingState, filtersState, focusedPoiState, geoJsonState, geoState, hoveredBrandState, loggedUserState, metricState, poiMapColorState, recommendedRegionsState, selectedBrands, selectedTabState, selAggLayerState, selPOICategories, shimmerLiteState, showDrawerState, showMapColorState, spinnerState, userCurrencyState, overlayCountState, checkboxState, dynamicTogglesState, dynamicPoiTogglesState } from "../../states";
import { checkIsBrandSelected, checkOutOfBounds, getRoIBounds, processGeometry, useHandleError, getParentGeoId, cleanFilterTree, getFeatureZIndex, getHoverClickable, getGMapFillColor, getGMapFillOpacity, getGMapStrokeColor, getGMapStrokeWeight, getCurrGeoLevelName } from "../../helpers";
import { HoverPopup, StoryPopup, BrandPopup, MapLayers, HoverMarkers, PresenceHoverCard, GMapDeckOverlay, AggUnitControl } from ".";
import { Button, ButtonTheme, ButtonVariant, Heading, SpinnerLite, ToggleTabs, TypographyColor, TypographyType } from "../../ui-elements";
import { useLocation } from "react-router-dom";
import { DISTRICT_BOUNDARY_BG_STYLE, DISTRICT_BOUNDARY_STYLE, RESET_FIELD_ARRAY, ROI_RECTANGLE_SPEC, RouteConstants, VIEW_TYPES } from "../../constants";
import highlightedPin from '../../utils/images/highlightedNewPinkPin.png';
import pin from '../../utils/images/NewPinkPin.png';
import equal from 'fast-deep-equal';
import { LuArrowUpRight } from "react-icons/lu";
import { GeoId } from "../../types/geo.types";

interface MapContainerProps {
    spinnerLite: number;
    myPresence?: any[];
    stories?: any[];
    selectedStory?: any;
    isStreetViewEnabled?: boolean;
    toggleSwitch?: (e: any, name: any, value: boolean) => void;
    setMapSpinner: React.Dispatch<React.SetStateAction<number>>;
    setIsStreetViewEnabled?: (flag: boolean) => void;
    showHelperText?: boolean;
    setShowHelperText?: React.Dispatch<React.SetStateAction<boolean>>;
    setMapColorShimmer?: React.Dispatch<React.SetStateAction<boolean>>;
    mbpFilter?: any;
    selCatchmentType?: any;
    disableZoom?: boolean;
    catchment: any;
}

export const MapContainer: React.FC<MapContainerProps> = ({
    spinnerLite,
    myPresence,
    stories,
    selectedStory,
    isStreetViewEnabled,
    toggleSwitch,
    setMapSpinner,
    setIsStreetViewEnabled,
    showHelperText,
    setShowHelperText,
    setMapColorShimmer,
    mbpFilter,
    selCatchmentType,
    catchment,
    disableZoom = false,
}) => {
    const COUNTRY_MAP_ID = process.env.REACT_APP_MAP_ID;
    const SILVER_MAP_ID = process.env.REACT_APP_MAP_ID_1;
    const MAP_TYPE_ID = process.env.REACT_APP_MAP_TYPE_ID;
    const map = useMap();
    const { pathname } = useLocation();
    const mapService = useMapsService();
    const [selectedGeo, setselectedGeo] = useRecoilState(geoState);
    const cifType = useRecoilValue(cifTypeState);
    const loggedUser = useRecoilValue(loggedUserState);
    const isChecked = useRecoilValue(checkboxState);
    const selAggLayer = useRecoilValue(selAggLayerState);
    const geoJSON = useRecoilValue(geoJsonState);
    const setGeoJSON = useSetRecoilState(geoJsonState);
    const poiColors = useRecoilValue(poiMapColorState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const selCurrency = useRecoilValue(userCurrencyState);
    const showMapColor = useRecoilValue(showMapColorState);
    const dynamicToggles = useRecoilValue(dynamicTogglesState);
    const dynamicPoiToggles = useRecoilValue(dynamicPoiTogglesState);
    const choroplethMode = useRecoilValue(choroplethModeState);
    const [showCategoricalToast, setShowCategoricalToast] = useState(false);
    const [hoverData, setHoverData] = useState<any>();
    const [featuredStories, setFeaturedStories] = useState<any[]>([]);
    const [center, setCenter] = useState({ lat: 20.5937, lng: 78.9629 });
    const [focused, setFocused] = useState(0);
    const [hoveredBrand, setHoveredBrand] = useRecoilState<any>(hoveredBrandState);
    const [openedStories, setOpenedStories] = useState<number[]>([]);
    const { getErrorMsg } = useHandleError();
    const [spinner, setSpinner] = useRecoilState(spinnerState);
    const [shimmerLite, setShimmerLite] = useRecoilState(shimmerLiteState);
    const [mapProps, setMapProps] = useState<any>();
    const [mapType, setMapType] = useState<'roadmap' | 'hybrid' | 'map' | 'dark'>(localStorage.getItem("map-style") as 'roadmap' | 'hybrid' | 'map' | 'dark' || 'roadmap');
    const [subDistrictDefaultMapInfo, setSubDistrictDefaultMapInfo] = useState<{ bounds: any, zoom: number }>({ bounds: null, zoom: 1 });
    const [brands, setBrands] = useRecoilState(brandList);
    const [selBrands, setSelBrands] = useRecoilState(selectedBrands);
    const [hoveredPresence, setHoveredPresence] = useState<any | null>(null);
    const [zoom, setZoom] = useState({ isZoomed: false, zoomLevel: 0, center: map?.getCenter() ?? { lat: 20.5937, lng: 78.9629 } });
    const [focusedPoi, setFocusedPoi] = useRecoilState(focusedPoiState);
    const focusedPoiRef = useRef(focusedPoi);
    const selMetric = useRecoilValue(metricState);
    const setError = useSetRecoilState(errorState);
    const filters = useRecoilValue<any[]>(filtersState);
    const filtersdefaults = useRecoilValue<any[]>(filterDefaultState);
    const [selectedTab, setSelectedTab] = useRecoilState(selectedTabState);
    const districtBoundariesRef = useRef<google.maps.Polyline[]>([]);
    const roiBoundsRef = useRef<{ north: number; south: number; east: number; west: number; } | null>(null);
    const outOfBoundSideRef = useRef<string[]>([]);
    const isFilterLoading = useRecoilValue(filterLoadingState);
    const poiBoundRef = useRef<{ north: number; south: number; east: number; west: number; } | null>(null);
    const mbpBoundRef = useRef<{ north: number; south: number; east: number; west: number; } | null>(null);
    const setRecommendedRegionsPayload = useSetRecoilState(recommendedRegionsState);
    const isHoveredOnMarker = useRef<{ poi: boolean, mbp: boolean }>({ poi: false, mbp: false })
    const isGeoFocusedRef = useRef<boolean>(false);
    const [focusedGeo, setFocusedGeo] = useState<any>(false);
    const [open, setOpen] = useRecoilState(showDrawerState);
    const mapPropsPayloadRef = useRef<any>(null);
    const overlayCount = useRecoilValue(overlayCountState);

    // Clear stale boundaries on mount so key-based remount (catchment toggle) always starts fresh
    useEffect(() => {
        setGeoJSON({ type: 'FeatureCollection', rootProperties: null, features: [] } as any);
    }, []);

    useEffect(() => {
        if (choroplethMode === 'categorical') {
            setShowCategoricalToast(true);
            const t = setTimeout(() => setShowCategoricalToast(false), 2000);
            return () => clearTimeout(t);
        }
    }, [choroplethMode]);

    // Re-add boundaries if Vector renderer silently cleared map.data while tab was hidden
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && map && geoJSON?.features?.length > 0) {
                let mapDataCount = 0;
                map.data.forEach(() => mapDataCount++);
                if (mapDataCount === 0 && addedFeaturesCountRef.current > 0) {
                    addedFeaturesCountRef.current = 0;
                    map.data.addGeoJson({ type: 'FeatureCollection', features: geoJSON.features });
                    addedFeaturesCountRef.current = geoJSON.features.length;
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [map, geoJSON]);

    // State for tracking SSE data loading
    const [boundaryLoadingState, setBoundaryLoadingState] = useState<{
        loading: boolean;
        firstChunkReceived: boolean;
    }>({ loading: false, firstChunkReceived: false });

    // Ref to accumulate features from SSE stream
    const accumulatedFeaturesRef = useRef<{
        rootProperties: any;
        features: any[];
    }>({ rootProperties: null, features: [] });

    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return geo_id === '1' ? 'EW-IN-IND-22-0' : geo_id;
    }, [selectedGeo]);

    const uniqueStories = Array.from(
        new globalThis.Map(
            stories?.map(story => [JSON.stringify(story.geometry.coordinates), story])
        ).values()
    );

    const normalizeLatLng = (latLng: google.maps.LatLng | undefined) =>
        latLng ? { lat: latLng.lat(), lng: latLng.lng() } : { lat: 20.5937, lng: 78.9629 };

    //fetch featured stories
    const fetchFeaturedStories = (geoCode: number) => {
        // setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        mapService.getFeaturedStories(geoCode, selCurrency).then(response => {
            setFeaturedStories(response.featuredStories || []);
        }).catch(error => {
            getErrorMsg(error);
        })
        // .finally(() => setSpinner(prev => ({ ...prev, count: prev.count - 1 })));
    }

    //fetch map props
    const fetchMapProps = (payload: any) => {
        // setMapSpinner(prev => prev + 1);
        mapService.getMapProperties(payload).then(response => {
            setMapProps(response);
        }).catch(error => {
            getErrorMsg(error);
        })
        // .finally(() => setMapSpinner(prev => prev - 1));
    }

    // toggle spotlights switch if all the spotlights have closed
    const handleCloseMapPopup = (index: number) => {
        if (toggleSwitch) {
            const opened = openedStories.filter(item => item !== index);
            if (opened.length === 0) {
                toggleSwitch(null, 'stories', false);
            }
            setOpenedStories(opened);
        }
    }

    const handleFocused = (index: number) => {
        setFocused(index);
    };

    const handleMapType = (type: 'roadmap' | 'hybrid' | 'map' | 'dark') => {
        setMapType(type);
        localStorage.setItem("map-style", type);
    }

    //rest bouding box and zoom
    const handleResetBound = () => {
        if (!map || !geoJSON?.features?.length) return;

        const bounds = new window.google.maps.LatLngBounds();
        const processCoords = (coords: any): void => {
            if (Array.isArray(coords[0])) coords.forEach(processCoords);
            else bounds.extend(new window.google.maps.LatLng(coords[1], coords[0]));
        };
        geoJSON.features.forEach((f: any) => {
            if (!f.properties?.is_adjacent) processCoords(f.geometry.coordinates);
        });

        if (!bounds.isEmpty()) map.fitBounds(bounds);

        const zoomOutButton: any = document.querySelector('button[title="Zoom out"]');
        if (zoomOutButton) {
            zoomOutButton.disabled = false;
            zoomOutButton.style.opacity = "1";
            zoomOutButton.style.cursor = "pointer";
        }
    }

    const onMouseOver = useCallback((event: any, map: google.maps.Map, selectedTab: number) => {
        if (!map || !mapProps || !mapProps.properties) return;

        if (selectedGeo.sub_district == geoCode && !isChecked?.subregionBoundaries) return;

        const feature = event.feature;
        const geoLevel = feature.getProperty('geoLevel');

        const { country, state, district, sub_district } = selectedGeo;
        const geo_id = sub_district || district || state || country;

        if (selectedGeo.state && (isHoveredOnMarker.current.mbp && !isGeoFocusedRef.current)) {
            setHoverData(null);
            return;
        }

        const latLng = event.latLng.toJSON();
        outOfBoundSideRef.current = checkOutOfBounds(latLng, roiBoundsRef.current);

        if (geo_id === district && ((selectedTab === 5 && geoLevel === 'sub_district') || selectedTab === 4 && geoLevel === 'pincode')) return;

        onHoverMap(feature, selectedTab, latLng, isGeoFocusedRef.current);
        const STYLE = {
            strokeColor: '#000000',
            strokeWeight: 0.5,
            fillOpacity: 0.5,
            zIndex: 10
        }

        if (getHoverClickable(selectedGeo, selectedTab, geoLevel, isChecked?.subregionBoundaries!)) {
            map?.data.overrideStyle(feature, STYLE);
        }
    }, [mapProps, focusedGeo, isChecked?.subregionBoundaries]);


    //display the geo info popup on hover over the geojson item
    const onHoverMap = (feature: any, selectedTab: number, latLng: any, isGeoFocused: boolean) => {
        if (!mapProps || !mapProps.properties || isGeoFocused) return;

        const geoLevel = feature?.getProperty('geoLevel');
        if (!getHoverClickable(selectedGeo, selectedTab, geoLevel, isChecked?.subregionBoundaries!)) return;

        const geoId = feature?.getProperty('geoId');
        const found: any = mapProps?.properties?.find((item: any) => item.geoId === geoId);

        if (found) {
            setHoverData((prev: any) => {
                if (prev?.geoId === geoId) {
                    return { ...prev, coordinates: latLng };
                } else {
                    return { ...found, coordinates: latLng };
                }
            });
        } else {
            setHoverData(null);
        }
    };

    const onMouseOut = useCallback((event: any, map: google.maps.Map) => {
        map?.data.revertStyle(event.feature);
        if (!isGeoFocusedRef.current) setHoverData(null);
    }, [mapProps]);

    //navigate to the clicked geo
    const onGeoClick = (event: any) => {
        const geoId = event.feature.getProperty('geoId');
        let geoLevel = event.feature.getProperty('geoLevel');

        if ((geoLevel === 'pincode' && selectedTab === 4)) return;

        if (geoLevel === 'pincode') geoLevel = 'sub_district';

        if (geoLevel == "village" && !isChecked?.subregionBoundaries) return;
        if (!selectedGeo.state) {
            const resetFields: any = {
                country: { state: '', district: '', sub_district: '', village: '' },
                state: { district: '', sub_district: '', village: '' },
                district: { sub_district: '', village: '' },
                sub_district: { village: '' }
            };
            const updatedGeo = {
                ...selectedGeo,
                [geoLevel]: geoId,
                ...(resetFields[geoLevel] || {}),
            }

            setselectedGeo(updatedGeo);
            return;
        }

        // Close any open presence/poi card before opening region card

        setFocusedPoi({ type: null, data: null });
        setHoveredPresence(null);
        setHoveredBrand(null);
        isHoveredOnMarker.current.mbp = false;
        isHoveredOnMarker.current.poi = false;


        // Temporarily reset focus to allow hover data update
        isGeoFocusedRef.current = false;

        // Update hover data for the new region
        const latLng = event.latLng.toJSON();
        onHoverMap(event.feature, selectedTab, latLng, false);

        // Set focus back to true after updating
        setFocusedGeo(true);
        isGeoFocusedRef.current = true;
    }

    const handleCloseRegionCard = () => {
        isGeoFocusedRef.current = false;
        setFocusedGeo(false);
        setHoverData(null);
    }

    const handleZoomOutStyle = (zoom: number, map: google.maps.Map | null) => {
        const newZoom = map?.getZoom() ?? zoom;

        const zoomOutButton: any = document.querySelector('button[title="Zoom out"]');

        const zoomDiff = newZoom <= zoom - 1;

        setZoom({ isZoomed: true, zoomLevel: map?.getZoom() || 0, center: normalizeLatLng(map?.getCenter()) });

        if (zoomOutButton) {
            zoomOutButton.disabled = !!zoomDiff;
            zoomOutButton.style.opacity = zoomDiff ? "0.5" : "1";
            zoomOutButton.style.cursor = zoomDiff ? "default" : "pointer";
        }

        if (map) {
            if (zoomDiff) {
                map.setOptions({
                    gestureHandling: 'greedy',
                    minZoom: zoom - 1,
                    draggable: false
                });
            } else {
                // Restore default behavior
                map.setOptions({
                    gestureHandling: "auto",
                    minZoom: undefined,
                    draggable: true
                });
            }
        }
    };

    const onPanChange = (map: google.maps.Map | null) => {
        if (map) {
            poiBoundRef.current = getRoIBounds(map, ROI_RECTANGLE_SPEC.POI);
            mbpBoundRef.current = getRoIBounds(map, ROI_RECTANGLE_SPEC.MBP);
        }
    };

    const handleDragEnd = (map: google.maps.Map | null) => {
        const center = map?.getCenter();
        if (center) {
            setZoom(prev => ({
                ...prev,
                center: { lat: center.lat(), lng: center.lng() }
            }));
        }
    };

    const handleWheel = (event: WheelEvent) => {
        // const zoomDiff = (map?.getZoom() || 0) <= subDistrictDefaultMapInfo.zoom - 1;
        setZoom({ isZoomed: true, zoomLevel: map?.getZoom() || 0, center: normalizeLatLng(map?.getCenter()) });

        if (event.deltaY < 0 && map) {
            event.preventDefault();

            map.setOptions({
                gestureHandling: "auto",
                minZoom: undefined,
                draggable: true
            });
        }
    };

    const handleTouch = (event: TouchEvent) => {
        setZoom({ isZoomed: true, zoomLevel: map?.getZoom() || 0, center: normalizeLatLng(map?.getCenter()) });

        if (event.touches.length > 1) {
            const zoomDiff = (map?.getZoom() || 0) <= subDistrictDefaultMapInfo.zoom - 1;

            if (!zoomDiff && map) {
                event.preventDefault();

                map.setOptions({
                    gestureHandling: "auto",
                    minZoom: undefined,
                    draggable: true
                });
            }
        }
    };

    const handleNavigation = (e: any, resetFields: any, navigation: any) => {
        e.stopPropagation();
        handleCloseRegionCard();
        setFocusedPoi({ type: null, data: null });
        setHoveredBrand(null);
        setHoveredPresence(null);

        const selGeo: Record<string, any> = {};

        resetFields.forEach((key: string) => {
            if (key === 'pincode' || key === 'sub_district')
                key = selectedTab === 4 ? 'sub_district' : 'pincode';
            let value = navigation[key];

            if (value !== undefined && value !== null && value !== "") {
                if (key === 'pincode') selGeo['sub_district'] = value;
                else selGeo[key] = value;
            }
        });

        setselectedGeo(selGeo);
    }

    const handleNavigationFromRegionCard = (e: any, navigation: any) => {
        e.stopPropagation();
        handleCloseRegionCard();
        setFocusedPoi({ type: null, data: null });
        setHoveredBrand(null);
        setHoveredPresence(null);

        const selGeo: Record<string, any> = {};

        RESET_FIELD_ARRAY['all'].forEach((key: string) => {
            let value = navigation[key];

            if (value !== undefined && value !== null && value !== "") {
                if (key === 'pincode') selGeo['sub_district'] = value;
                else selGeo[key] = value;
            }
        });
        setselectedGeo(selGeo);
    }

    const getPayload = (geoId: GeoId) => {

        return {
            geoId,
            type: cifType,
            currency: selCurrency,
            ...selCategoryList,
        }
    }

    //fetch featured stories and map properties
    useEffect(() => {
        if (selectedGeo && loggedUser.company && pathname === RouteConstants.root) {
            fetchFeaturedStories(geoCode);
        }
    }, [selectedGeo, loggedUser.company, selCurrency]);


    // Helper function to merge features and update geoJSON state
    const mergeAndUpdateGeoJSON = () => {
        const { rootProperties, features } = accumulatedFeaturesRef.current;

        const newGeoJSON = {
            type: 'FeatureCollection',
            rootProperties,
            features
        };

        setGeoJSON(newGeoJSON);
    };

    // Fetch geojson using React Query with SSE progressive loading
    useQuery({
        queryKey: ['map', geoCode, isChecked?.catchments ? selCatchmentType?.key : null, (geoCode) !== "EW-IN-IND-22-0" && isChecked?.adjacency],
        queryFn: async ({ signal }) => {
            if (!geoCode) {
                return null;
            }

            setBoundaryLoadingState({ loading: true, firstChunkReceived: false });

            return mapService.getMap(
                (geoCode),
                false, // hex parameter - using false for backward compatibility
                isChecked?.catchments ? selCatchmentType?.key : null,
                (geoCode) != "EW-IN-IND-22-0" && isChecked?.adjacency,
                signal,
                (progressData) => {
                    // Handle progress updates from SSE stream
                    if (progressData.type === 'rootProperties') {
                        accumulatedFeaturesRef.current.rootProperties = progressData.data;
                        mergeAndUpdateGeoJSON();
                    } else if (progressData.features) {
                        accumulatedFeaturesRef.current.features = [
                            ...accumulatedFeaturesRef.current.features,
                            ...progressData.features
                        ];

                        // Mark first chunk as received
                        if (!boundaryLoadingState.firstChunkReceived) {
                            setBoundaryLoadingState({ loading: true, firstChunkReceived: true });
                        }

                        mergeAndUpdateGeoJSON();
                    } else if (progressData.status === 'complete') {
                        setBoundaryLoadingState({ loading: false, firstChunkReceived: true });
                    }
                }
            );
        },
        enabled: !!selectedGeo && !!selCurrency && !!geoCode,
        staleTime: 0,
        gcTime: 0,
        retry: false,
        refetchOnReconnect: false,
    });

    // Reset accumulated features when geoCode changes
    useEffect(() => {
        accumulatedFeaturesRef.current = {
            rootProperties: null,
            features: []
        };
        setBoundaryLoadingState({ loading: false, firstChunkReceived: false });
    }, [geoCode]);

    // Handle side effects when geo changes
    useEffect(() => {
        setZoom({ isZoomed: false, zoomLevel: map?.getZoom() || 0, center: normalizeLatLng(map?.getCenter()) });

        // Clear MBP/POI states when navigating between geo levels
        setHoveredPresence(null);
        setHoveredBrand(null);
        setFocusedPoi({ type: null, data: null });

        // Handle map options
        if (!selectedGeo.sub_district && map) {
            map.setOptions({
                gestureHandling: 'none',
                minZoom: undefined,
                draggable: false
            });
        } else if (selectedGeo.sub_district && map) {
            map.setOptions({
                gestureHandling: 'auto',
                draggable: true
            });
        }
        handleCloseRegionCard();
    }, [selectedGeo, isChecked?.adjacency, isChecked?.catchments, selCatchmentType]);

    // Track the number of features already added to prevent re-rendering
    const addedFeaturesCountRef = useRef(0);
    const currentGeoIdRef = useRef<GeoId | null>(null);


    // Display geoJSON data in the map
    useEffect(() => {
        if (Object.keys(geoJSON).length && map) {
            setHoverData(null);

            const currentGeoId = geoJSON?.rootProperties?.geoId ? (geoJSON.rootProperties.geoId) : null;

            let mapDataCount = 0;
            map.data.forEach(() => mapDataCount++);

            // If it's a new geoId, clear everything and reset
            if (currentGeoId !== currentGeoIdRef.current) {
                map?.data.forEach((feature) => {
                    map.data.remove(feature);
                });
                addedFeaturesCountRef.current = 0;
                currentGeoIdRef.current = currentGeoId;
            }

            // Guard: Vector renderer (QA env) can silently clear map.data while idle.
            // If map.data is empty but our counter says features were added, reset so
            // the next branch re-adds them.
            if (mapDataCount === 0 && addedFeaturesCountRef.current > 0) {
                addedFeaturesCountRef.current = 0;
            }

            // Only add new features that haven't been added yet
            if (geoJSON.features.length > addedFeaturesCountRef.current) {
                const newFeatures = geoJSON.features.slice(addedFeaturesCountRef.current);
                map?.data.addGeoJson({ type: 'FeatureCollection', features: newFeatures });
                addedFeaturesCountRef.current = geoJSON.features.length;
            }

            let onClickListener: google.maps.MapsEventListener | undefined;
            let onMouseOverListener: google.maps.MapsEventListener | undefined;
            let onMouseOutListener: google.maps.MapsEventListener | undefined;
            let boundsListener: google.maps.MapsEventListener | undefined;
            let zoomListener: google.maps.MapsEventListener | undefined;
            let dragListener: google.maps.MapsEventListener | undefined;
            let onPanChangeListener: google.maps.MapsEventListener | undefined;

            if (pathname === RouteConstants.root) {
                onMouseOverListener = map?.data.addListener('mousemove', (event: any) => onMouseOver(event, map, selectedTab));
                onMouseOutListener = map?.data.addListener('mouseout', (event: any) => onMouseOut(event, map));
                onClickListener = map?.data.addListener('click', onGeoClick);
            }

            const processCoordinates = (coordinates: any): void => {
                if (Array.isArray(coordinates[0])) {
                    // Multi-part geometry, like a polygon with holes or a multi-line string
                    coordinates.forEach((coordSet: any) => {
                        processCoordinates(coordSet);
                    });
                } else {
                    // Single set of coordinates
                    bounds.extend(new window.google.maps.LatLng(coordinates[1], coordinates[0]));
                }
            };

            const bounds = new window.google.maps.LatLngBounds();
            geoJSON.features.forEach((feature: any) => {
                if ((feature?.properties?.is_adjacent && isChecked?.adjacency && feature?.properties?.geoLevel !== getCurrGeoLevelName(selectedGeo)) || !isChecked?.adjacency || selectedGeo?.village)
                    processCoordinates(feature.geometry.coordinates);
            });

            if (!selectedGeo.sub_district && map) {
                map.setOptions({
                    gestureHandling: 'none',
                    minZoom: undefined,
                    draggable: false
                });
            } else if (selectedGeo.sub_district && map) {
                // Only set 'auto' if zoom is not disabled, otherwise set 'none' after fitBounds
                const shouldDisableZoom = disableZoom && (selectedGeo.sub_district || selectedGeo.village);
                if (!shouldDisableZoom) {
                    map.setOptions({
                        gestureHandling: 'auto',
                        draggable: true
                    });
                }
            }
            // Set map center and zoom level based on bounding box
            map?.fitBounds(bounds, 20);

            // const rectangle = new google.maps.Rectangle({
            //     bounds: bounds,
            //     strokeColor: '#FF0000',
            //     strokeOpacity: 0.8,
            //     strokeWeight: 2,
            //     fillColor: '#FFFFFF',
            //     fillOpacity: 0,
            //     map: map
            // });

            const polygons: any[] = [];

            map.data.forEach((feature) => {
                const geometry = feature.getGeometry();
                const type = geometry?.getType();

                if (type === "Polygon" || type === "MultiPolygon") {
                    polygons.push(feature);
                }
            });

            setTimeout(() => {
                if (zoom.isZoomed && selectedGeo.sub_district) {
                    map?.setZoom(zoom.zoomLevel);
                    map?.setCenter(zoom.center);
                    if (zoom.zoomLevel <= subDistrictDefaultMapInfo.zoom - 1) {
                        map.setOptions({
                            gestureHandling: 'greedy',
                            minZoom: subDistrictDefaultMapInfo.zoom - 1,
                            draggable: false
                        });
                    }
                }
            }, 0);

            boundsListener = window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                if (!selectedGeo.village) {
                    roiBoundsRef.current = getRoIBounds(map, ROI_RECTANGLE_SPEC.HOVER);
                    mbpBoundRef.current = getRoIBounds(map, ROI_RECTANGLE_SPEC.MBP);
                }

                // if (pathname !== '/') {
                //     if (!selectedGeo.state) map.setZoom((map.getZoom()! || 0) + 0.625); //reduced zoom factor from 0.75 to 0.725 (TAR-750) //UPDATED - TAR-1087 - reduced to 0.625 only for country level
                //     else map.setZoom((map.getZoom()! || 0) + 0.4);
                // }

                // Check if zoom should be disabled regardless of pathname
                const shouldDisableZoom = disableZoom && (selectedGeo.sub_district || selectedGeo.village);

                if (pathname === '/' && selectedGeo.sub_district && geoJSON?.rootProperties && ((selectedGeo.sub_district) === (geoJSON.rootProperties.geoId) || (selectedGeo.village) === (geoJSON.rootProperties.geoId))) {
                    const initialZoom = !zoom.isZoomed ? (map.getZoom()! || 0) : subDistrictDefaultMapInfo.zoom;

                    if (zoomListener) zoomListener.remove();

                    if (!shouldDisableZoom) {
                        zoomListener = map?.addListener("zoom_changed", () => handleZoomOutStyle(initialZoom, map));
                        onPanChangeListener = map?.addListener("idle", () => onPanChange(map));
                        dragListener = map?.addListener('dragend', () => handleDragEnd(map));

                        if (!zoom.isZoomed) setSubDistrictDefaultMapInfo({ bounds, zoom: initialZoom });

                        map.getDiv().addEventListener("wheel", handleWheel, { passive: false });
                        map.getDiv().addEventListener("touchstart", handleTouch, { passive: false });
                    }
                }

                // Apply zoom restrictions AFTER bounds are set (for both home and dashboard)
                if (shouldDisableZoom) {
                    map.setOptions({
                        gestureHandling: 'none',
                        scrollwheel: false,
                        disableDoubleClickZoom: true,
                        draggable: false
                    });
                }
            });

            setCenter({ lat: bounds?.getCenter()?.lat(), lng: bounds?.getCenter()?.lng() });

            // Cleanup function
            return () => {
                if (pathname === RouteConstants.root) {
                    onMouseOverListener?.remove();
                    onMouseOutListener?.remove();
                    onClickListener?.remove();
                    // mouseOver?.remove();

                    map.getDiv().removeEventListener("wheel", handleWheel);
                    map.getDiv().removeEventListener("touchstart", handleTouch);

                    if (zoomListener) zoomListener?.remove();
                    if (onPanChangeListener) onPanChangeListener?.remove();
                    if (dragListener) dragListener?.remove();
                }
                if (boundsListener) boundsListener.remove();
            };
        }
    }, [map, geoJSON, mapProps, selectedTab, geoCode, isChecked?.subregionBoundaries]);

    // Debounce the map color query key to batch rapid successive state changes
    // (e.g. selectedGeo and selAggLayer change in separate renders on geo navigation)
    const [debouncedMapColorKey, setDebouncedMapColorKey] = useState({
        geoCode, selCategoryList, selCurrency, selMetric, filtersdefaults,
        adjacency: isChecked?.adjacency, catchments: isChecked?.catchments,
        catchmentTypeKey: selCatchmentType?.key, selectedTab, selectedGeo,
        dynamicToggles,
    });
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedMapColorKey({
            geoCode, selCategoryList, selCurrency, selMetric, filtersdefaults,
            adjacency: isChecked?.adjacency, catchments: isChecked?.catchments,
            catchmentTypeKey: selCatchmentType?.key, selectedTab, selectedGeo,
            dynamicToggles,
        }), 50);
        return () => clearTimeout(timer);
    }, [geoCode, selCategoryList, selCurrency, selMetric, filtersdefaults, isChecked?.adjacency, isChecked?.catchments, selCatchmentType?.key, selectedTab, selectedGeo, dynamicToggles]);

    // Fetch poi map color using React Query - handles race conditions automatically
    const { isFetching: isMapColorFetching } = useQuery({
        queryKey: ['mapColor', debouncedMapColorKey],
        queryFn: async ({ signal }) => {
            const { category, subcategory } = selCategoryList;
            const oldfilters = JSON.parse(localStorage?.getItem("filters")!);

            if (!geoCode || category.length === 0 || subcategory.length === 0 || !selCurrency || isFilterLoading || !(Array.isArray(oldfilters))) return null;

            const selMetrics = JSON.parse(localStorage?.getItem("selMetrics") || '{}');

            const metric_type = (!selMetrics || Object.values(selMetrics).filter((item: any) => !!item).length === 0)
                ? cifType.map(item => ({ key: item, ismetric: false }))
                : Object.values(selMetrics)
                    .map((metric: any) => {
                        const { title, ...rest } = metric || {};
                        return { ...rest };
                    }) || [];

            const choroplethMetrics = Object.entries(dynamicToggles).map(([id, isOn]) => ({ key:id, ismetric: false, isRank: isOn }));
            const metric_type_final = [...metric_type, ...choroplethMetrics];

            const filters_new = filters.map(cleanFilterTree);

            if (!selectedGeo?.village && filters_new.length === 0) return null;

            const payload = {
                subcategory,
                metric_type: metric_type_final,
                filters: filters_new,
                currency: selCurrency,
                geoId: (geoCode),
                adjacency: isChecked?.adjacency || false,
                catchment: isChecked?.catchments ? [selCatchmentType?.title] : [],
            };

            if (geoCode !== (selectedGeo.village)) {
                setRecommendedRegionsPayload((prev: any) => ({
                    ...prev,
                    ...getPayload((geoCode)),
                    metric_type: metric_type_final,
                    filters: filters_new,
                    adjacency: isChecked?.adjacency || false,
                    catchment: isChecked?.catchments && selCatchmentType ? [selCatchmentType?.title] : [],
                    ...(selectedGeo?.district && { sublevel: selectedTab }),
                }));
            }

            const res = await mapService.getMapPoiColor(payload, signal);
            // Apply to entire filters array
            const cleanedFilters = filters.map(cleanFilterTree);
            localStorage.setItem('filters', JSON.stringify(cleanedFilters));
            return res;
        },
        enabled: !isFilterLoading,
        staleTime: 50,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (setMapColorShimmer) setMapColorShimmer(isMapColorFetching);
    }, [isMapColorFetching]);

    useEffect(() => {
        const { category, subcategory } = selCategoryList;

        if ((category.length > 0 || subcategory.length > 0) && geoCode !== (selectedGeo.village) && mbpFilter && pathname === RouteConstants.root) {
            const { parent, type } = getParentGeoId((geoCode), selectedGeo, selectedTab);
            const currentPayload = {
                ...getPayload((geoCode)),
                mbp_filter: JSON.parse(localStorage?.getItem("mbp-filter")!)?.data,
                parentgeoId: parent,
                adjacency: (geoCode) !== "EW-IN-IND-22-0" && isChecked?.adjacency || false,
                ...(isChecked?.catchments && { catchment: selCatchmentType?.title }),
                ...dynamicToggles,
            };

            // Only call API if payload has changed
            const isPayloadEqual = equal(mapPropsPayloadRef.current, currentPayload);

            if (!isPayloadEqual) {
                mapPropsPayloadRef.current = currentPayload;
                fetchMapProps(currentPayload);
            }
        }
    }, [selCategoryList, geoCode, selCurrency, mbpFilter, isChecked?.adjacency, isChecked?.catchments, selCatchmentType, dynamicToggles]);

    //handle map colors
    useEffect(() => {
        if (map && map.data && cifType && geoJSON) {

            districtBoundariesRef.current.forEach(polyline => polyline.setMap(null));
            districtBoundariesRef.current = [];

            const getStyle = (feature: any) => {
                const geoLevel = feature?.getProperty('geoLevel');
                const geoId = feature?.getProperty('geoId');
                const adjacency = feature?.getProperty('is_adjacent');

                const totalOverlayCount = Object.values(overlayCount || {}).reduce((acc: number, item) => acc + (item as number), 0);
                const defaultColor = totalOverlayCount >= 1 ? '#BEBEBE' : '#FFFFFF';
                const hasDynamicToggle = Object.values(dynamicToggles).some(v => v);
                const fillColor = (cifType.length > 0 || hasDynamicToggle) && poiColors?.colorProperties?.length > 0 ?
                    poiColors.colorProperties.find(item => item.geoId === geoId)?.color || defaultColor : defaultColor;

                if ((selectedGeo.district) === geoCode && selectedTab === 5 && geoLevel === 'district' && !adjacency) {
                    const geometry = feature.getGeometry();
                    const coordinateGroups = processGeometry(geometry);

                    coordinateGroups.forEach((coords) => {
                        const boundary = new google.maps.Polyline({
                            path: Array.isArray(coords) ? coords : [coords],
                            map: map,
                            ...DISTRICT_BOUNDARY_STYLE
                        });
                        districtBoundariesRef.current.push(boundary);
                    });

                    coordinateGroups.forEach((coords) => {
                        const boundary = new google.maps.Polyline({
                            path: Array.isArray(coords) ? coords : [coords],
                            map: map,
                            ...DISTRICT_BOUNDARY_BG_STYLE
                        });
                        districtBoundariesRef.current.push(boundary);
                    });
                }

                const isEventEnabled = getHoverClickable(selectedGeo, selectedTab, geoLevel, isChecked?.subregionBoundaries!);

                const style = {
                    fillColor: getGMapFillColor(selectedGeo, selectedTab, geoLevel, fillColor, totalOverlayCount),
                    fillOpacity: selAggLayer === null || (cifType.length === 0 && !hasDynamicToggle) ? 0 : getGMapFillOpacity(selectedGeo, selectedTab, geoLevel, isChecked?.subregionBoundaries, cifType.length, isChecked?.catchments),
                    strokeColor: getGMapStrokeColor(selectedGeo, geoLevel, adjacency),
                    strokeWeight: getGMapStrokeWeight(selectedGeo, selectedTab, geoLevel, geoId, adjacency, isChecked?.subregionBoundaries),
                    zIndex: getFeatureZIndex(selectedGeo, selectedTab, geoLevel) ? 5 : 1,
                    clickable: isEventEnabled,
                    hoverable: isEventEnabled,
                };
                return style;
            };
            map.data.setStyle(getStyle);
        }

        return () => {
            districtBoundariesRef.current.forEach(polyline => polyline.setMap(null));
            districtBoundariesRef.current = [];
        };
    }, [map, geoJSON, poiColors, cifType, selectedGeo, showMapColor, selectedTab, isChecked?.subregionBoundaries, selAggLayer, dynamicToggles, overlayCount, choroplethMode]);

    useEffect(() => {
        if (featuredStories.length > 0 && isChecked?.spotlight && !selectedGeo.district) {
            setOpenedStories(featuredStories.map((_, index) => index));
        }
    }, [featuredStories?.length, isChecked?.spotlight]);

    useEffect(() => {
        if (map) {
            const streetView = map.getStreetView();
            if (streetView.getVisible()) {
                streetView.setVisible(false);
            }
        }
        if (geoCode === (selectedGeo.sub_district) && isFilterLoading) return;

        if (pathname !== RouteConstants.root && cifType && (geoCode === (selectedGeo.sub_district) || geoCode === (selectedGeo.village)) && (selCategoryList.category.length > 0 || selCategoryList.subcategory.length > 0)) {
            // setSpinner(prev => ({ ...prev, spinners: [...prev.spinners, 'getBrands'] }))
            setShimmerLite((prev: string[]) => [...prev, "getBrands"]);
            const { parent, type } = getParentGeoId(geoCode, selectedGeo, selectedTab);
            let payload: any = { ...getPayload(geoCode), parentId: parent };

            // if (geoCode === Number(selectedGeo.sub_district)) {
            //     payload = { ...payload, filters };
            // }
            mapService.getBrands(payload)
                .then(res => {
                    setBrands(res);
                    setSelBrands([]);
                }).
                catch(error => console.log(error))
                .finally(() =>
                    // setSpinner(prev => ({ ...prev, spinners: prev.spinners.filter(s => s !== 'getBrands') }))
                    setShimmerLite((prev: string[]) => prev.filter(item => item !== "getBrands"))
                );
        }
    }, [geoCode, selCategoryList, isChecked?.adjacency]);

    //exit full screen on clicking the street view navigate back button
    useEffect(() => {
        if (map) {
            const streetView = map.getStreetView();
            let dateControlDiv: HTMLDivElement | null = null;

            const formatImageDate = (dateString: string): string => {
                // Parse date string like "2023-06" or "2023"
                const parts = dateString.split('-');
                const year = parts[0];
                const month = parts[1];

                if (month) {
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const monthIndex = parseInt(month, 10) - 1;
                    return `Captured on: ${monthNames[monthIndex]} ${year}`;
                }

                return `Captured on: ${year}`;
            };

            const createTimestampControl = () => {
                if (dateControlDiv) return; // Already created

                // Enable time machine controls for historical imagery
                streetView.setOptions({
                    imageDateControl: true,
                });

                // Create custom control for timestamp
                dateControlDiv = document.createElement('div');
                dateControlDiv.style.borderRadius = '2px';
                dateControlDiv.style.background = 'rgba(34, 34, 34, 0.70)';
                dateControlDiv.style.color = 'white';
                dateControlDiv.style.padding = '8px 12px';
                dateControlDiv.style.margin = '10px';
                dateControlDiv.style.fontSize = '12px';
                dateControlDiv.style.fontWeight = '500';
                dateControlDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
                dateControlDiv.style.fontFamily = 'Roboto, Arial, sans-serif';
                dateControlDiv.textContent = 'Loading...';

                // Add to Street View controls
                streetView.controls[google.maps.ControlPosition.LEFT_TOP].push(dateControlDiv);

                // Fetch initial panorama data
                const position = streetView.getPosition();
                if (position) {
                    const streetViewService = new google.maps.StreetViewService();
                    streetViewService.getPanorama(
                        { location: position, radius: 50 },
                        (data, status) => {
                            if (status === google.maps.StreetViewStatus.OK && data?.imageDate && dateControlDiv) {
                                dateControlDiv.textContent = formatImageDate(data.imageDate);
                            } else if (dateControlDiv) {
                                dateControlDiv.textContent = 'Image date unavailable';
                            }
                        }
                    );
                }
            };

            const removeTimestampControl = () => {
                if (dateControlDiv) {
                    const controls = streetView.controls[google.maps.ControlPosition.LEFT_TOP];
                    const index = controls.getArray().indexOf(dateControlDiv);
                    if (index > -1) {
                        controls.removeAt(index);
                    }
                    dateControlDiv = null;
                }
            };

            let streetViewListener = streetView.addListener("visible_changed", () => {
                if (setIsStreetViewEnabled) {
                    setIsStreetViewEnabled(streetView.getVisible())
                }

                if (!streetView.getVisible() && document.fullscreenElement) {
                    document.exitFullscreen();
                    removeTimestampControl();
                } else if (streetView.getVisible()) {
                    createTimestampControl();
                }
            });

            // Listen for position changes to get panorama metadata
            let positionListener = streetView.addListener("position_changed", () => {
                const position = streetView.getPosition();
                if (position && dateControlDiv) {
                    const streetViewService = new google.maps.StreetViewService();
                    streetViewService.getPanorama(
                        { location: position, radius: 50 },
                        (data, status) => {
                            if (status === google.maps.StreetViewStatus.OK && data?.imageDate && dateControlDiv) {
                                dateControlDiv.textContent = formatImageDate(data.imageDate);
                            } else if (dateControlDiv) {
                                dateControlDiv.textContent = 'Image date unavailable';
                            }
                        }
                    );
                }
            });

            // Check if street view is already visible when component mounts
            if (streetView.getVisible()) {
                createTimestampControl();
            }

            return () => {
                streetViewListener.remove();
                positionListener.remove();
                removeTimestampControl();
            };
        }
    }, [map]);

    const highlightedPoIs = useMemo(() => {
        if (!selectedGeo.sub_district || brands?.poimarker?.length === 0) return [];

        return brands?.poimarker?.flatMap((brand) =>
            brand.branches.map((branch: any) => ({
                ...branch,
                isSelected: checkIsBrandSelected(branch.branchId, selBrands),
            }))
        );
    }, [brands, selBrands]);

    const otherMarkers = useMemo(() => {
        const result: Record<string, any[]> = {};
        Object.entries(brands || {}).forEach(([key, value]) => {
            if (key !== 'poimarker') result[key] = value;
        });
        return result;
    }, [brands]);

    useEffect(() => {
        focusedPoiRef.current = focusedPoi || { type: null, data: null };
    }, [focusedPoi]);

    useEffect(() => {
        if (pathname === RouteConstants.root) {
            if (poiColors?.rootProperties?.filterProperties?.isAdjusted && !isChecked?.catchments) {
                topRightToast.info(`Filters have been adjusted to match this region's data range`);
            }
            if (poiColors?.rootProperties?.metricProperties && !poiColors?.rootProperties?.metricProperties?.hasData) {
                // setError({ type: 'MapInfo', message: `We're unable to compute this normalised metric with the current data layers. Please revise your chosen data layers.` });
            topRightToast.info(
                            "We're unable to compute this normalised metric with the current data layers. Please revise your chosen data layers."
                        )
            }
        }
    }, [poiColors]);

    const handlePopupHover = (type: string, poi?: any, focusedPoi?: any) => {
        // Always update marker state for click detection
        isHoveredOnMarker.current = poi ? { ...isHoveredOnMarker.current, [type]: true } : { ...isHoveredOnMarker.current, [type]: false };

        // Don't show hover popup when a card is focused or region is focused
        if (focusedPoi?.type || isGeoFocusedRef.current) {
            return;
        }

        if (type === 'mbp') {
            setHoveredPresence(poi || null);
            setHoveredBrand(null);
        } else {
            // 'poi' and all other marker types
            setHoveredBrand(poi ? { ...poi, _markerType: type } : null);
            setHoveredPresence(null);
        }
    };

    const handlePoiClick = (type: string | null, data: any, screenCoord?: number[]) => {
        // Close any open region hover card before opening presence/poi card
        if (isGeoFocusedRef.current) {
            handleCloseRegionCard();
        }
        // Close region hover card when clicking on a marker
        setHoverData(null);

        // First clear both hover states to ensure clean slate
        setHoveredPresence(null);
        setHoveredBrand(null);

        // Then set the clicked item as hovered so it displays
        if (type === 'mbp') {
            setTimeout(() => setHoveredPresence(data), 0);
        } else if (type) {
            // 'poi' and all other marker types
            setTimeout(() => setHoveredBrand({ ...data, _markerType: type }), 0);
        }

        // Always set the new focused POI/presence to show close button and track focused state
        setFocusedPoi({ type, data });
    }

    const handlePoiMouseLeave = () => {
        if (!focusedPoi || !focusedPoi.type || focusedPoi.type !== 'poi') {
            setHoveredBrand(null);
        }
        if (!focusedPoi || !focusedPoi.type || focusedPoi.type !== 'mbp') {
            setHoveredBrand(null);
        }
    }

    const handlePoiClose = (e: any) => {
        e.stopPropagation();
        if (focusedPoi) {
            setFocusedPoi({ type: null, data: null });
            setHoveredPresence(null);
            isHoveredOnMarker.current.mbp = false
        }
    }

    const handleChangeTab = (val: number) => {
        setSelectedTab(val);
        localStorage.setItem("selectedTab", JSON.stringify(val));

    }

    const renderToggleTabs = () => { return selectedGeo.district && !selectedGeo?.sub_district && pathname === RouteConstants.root && !isStreetViewEnabled }
    return (
        <>
            {/* <button onClick={() => setOpen(!open)}>
                {open ? "Close Drawer" : "Open Drawer"}
            </button> */}
            {/* <Drawer open={open} setHeight={setHeight} height={height} setOpen={setOpen} /> */}

            <div className={mapType === 'dark' ? 'dark-zoom-controls' : ''} style={{ height: '100%', width: '100%', position: 'relative' }}>
                { /* To be rectified  */}            {(false) ?
                    <SpinnerLite /> :
                    <Map
                        // style={{ opacity: spinner.spinners.length === 0 && (spinnerLite > 0 || shimmerLite.includes('getBrands')) ? "0.3" : 1 }}
                        colorScheme={selectedGeo.sub_district && mapType == "dark" ? "DARK" : "LIGHT"}
                        // mapTypeId={selectedGeo.sub_district ? mapType : 'roadmap'}
                        // mapId={selectedGeo.state ? SILVER_MAP_ID : COUNTRY_MAP_ID}
                        mapTypeId={selectedGeo.sub_district && mapType === 'hybrid' ? 'hybrid' : 'roadmap'}
                        mapId={(mapType === 'map' && selectedGeo.sub_district) ? MAP_TYPE_ID : selectedGeo.state ? SILVER_MAP_ID : COUNTRY_MAP_ID}
                        defaultZoom={8}
                        defaultCenter={center}
                        disableDefaultUI={true}
                        mapTypeControl={false}
                        isFractionalZoomEnabled={!(disableZoom && (selectedGeo.sub_district || selectedGeo.village))}
                        keyboardShortcuts={false}
                        gestureHandling={
                            disableZoom && (selectedGeo.sub_district || selectedGeo.village)
                                ? 'cooperative'
                                : pathname === RouteConstants.root && selectedGeo.sub_district
                                    ? 'auto'
                                    : 'none'
                        }
                        scrollwheel={!(disableZoom && (selectedGeo.sub_district || selectedGeo.village))}
                        disableDoubleClickZoom={disableZoom && (selectedGeo.sub_district || selectedGeo.village)}
                        zoomControl={selectedGeo.sub_district && pathname === RouteConstants.root}
                        clickableIcons={false}
                        streetViewControl={!!selectedGeo.sub_district && pathname === RouteConstants.root}
                        streetViewControlOptions={window.google && window.google.maps && window.google.maps.ControlPosition
                            ? { position: 10 }
                            : undefined}
                        zoomControlOptions={window.google && window.google.maps && window.google.maps.ControlPosition
                            ? { position: 10 }
                            : undefined}
                        scaleControl={true}
                        controlSize={25}
                        onMouseout={() => {

                            setHoveredBrand(null);
                            setHoveredPresence(null);
                            setFocusedPoi({ type: null, data: null });
                            // isHoveredOnMarker.current.mbp = false
                            // isHoveredOnMarker.current.poi = false
                            handleCloseRegionCard();
                        }}
                    >
                        {/* reset zoom button */}
                        {selectedGeo.sub_district && pathname === RouteConstants.root && !isStreetViewEnabled &&
                            <div
                                className="reset-zoom-icon bg-white d-flex justify-content-center align-items-center"
                                title="Reset Zoom"
                                onClick={handleResetBound}
                            >
                                <span className='material-symbols-outlined cursor-pointer icon-gray-0'>restart_alt</span>
                            </div>
                        }

                        {/* {renderToggleTabs() &&
                            <div className="toggle-tabs-container pe-none">
                                <ToggleTabs
                                    options={VIEW_TYPES}
                                    selected={selectedTab} // selectedTab should now be 4 or 5
                                    onChange={(val) => {
                                        handleCloseRegionCard();
                                        handleChangeTab(val);
                                    }}
                                    onHover={handleCloseRegionCard}
                                />
                            </div>
                        } */}

                        {/* aggregation unit control */}
                        {pathname === RouteConstants.root && !isStreetViewEnabled &&
                            <AggUnitControl />
                        }

                        {/* map layers */}
                        {selectedGeo.sub_district && pathname === RouteConstants.root && !isStreetViewEnabled &&
                            <MapLayers
                                handleMapType={handleMapType}
                                handleCloseRegionCard={handleCloseRegionCard}
                                mapType={mapType}
                            />
                        }
                        {/* featured stories info window */}
                        {isChecked?.spotlight && featuredStories?.length > 0 && !selectedGeo.district && map &&
                            featuredStories.map((story: any, index: number) => {
                                return openedStories.includes(index) ?
                                    <InfoWindow
                                        key={story.properties.familyId}
                                        position={{
                                            lng: story.properties.geometry.coordinates[0],
                                            lat: story.properties.geometry.coordinates[1]
                                        }}
                                        zIndex={focused === index ? 50 : 0}
                                        disableAutoPan={true}
                                        shouldFocus={false}
                                        onCloseClick={() => handleCloseMapPopup(index)}
                                    >
                                        <StoryPopup
                                            story={story.properties}
                                            handleFocused={handleFocused}
                                            index={index}
                                        />
                                    </InfoWindow> : null
                            })
                        }

                        {/* geo info hover card */}
                        {hoverData && map &&
                            <AdvancedMarker
                                key='map-info-hover-card'
                                position={{
                                    lat: hoverData.coordinates.lat,
                                    lng: hoverData.coordinates.lng
                                }}
                                zIndex={200}
                            >
                                <div className={`map-prop-hover-card ${outOfBoundSideRef.current.join(" ")}`}>
                                    <HoverPopup
                                        classname="hover-popup"
                                        properties={hoverData}
                                        isGeoFocused={isGeoFocusedRef.current}
                                        handleClose={handleCloseRegionCard}
                                        handleNavigation={handleNavigationFromRegionCard}
                                    />
                                </div>
                            </AdvancedMarker>
                        }
                        {/* my business presence markers */}
                        {isChecked?.MBP && myPresence && myPresence?.length > 0 && map && (pathname === RouteConstants.root || pathname === RouteConstants.dashboard) && !selectedGeo.village &&
                            <HoverMarkers
                                type={'mbp'}
                                map={map}
                                pois={myPresence}
                                handlePopupHover={handlePopupHover}
                                isSubDistrict={selectedGeo.sub_district}
                                focusedPoi={focusedPoiRef}
                                handleClick={(type, data) => handlePoiClick(type, data)}
                                resetZIndex={!!hoverData}
                                currGeoCode={geoCode}
                            />
                        }

                        {hoveredPresence && pathname === RouteConstants.root && selectedGeo?.state &&
                            <AdvancedMarker
                                key={hoveredPresence?.unique_key}
                                position={{
                                    lat: hoveredPresence?.latitude,
                                    lng: hoveredPresence?.longitude
                                }}
                                zIndex={200}
                            >
                                <PresenceHoverCard
                                    classname="presence-hover-popup"
                                    presence={hoveredPresence}
                                    company_name={loggedUser.company_name}
                                    focusedPoi={focusedPoi}
                                    // handleClick={handlePoiClick}
                                    handleClose={handlePoiClose}
                                    handleNavigation={handleNavigation}
                                    roi={mbpBoundRef.current || null}
                                />
                            </AdvancedMarker>
                        }

                        {/* highlighted poi */}
                        {map && selectedGeo.sub_district && (
                            // <HoverMarkers
                            //     type={'poi'}
                            //     map={map}
                            //     pois={highlightedPoIs}
                            //     handlePopupHover={handlePopupHover}
                            //     isSubDistrict={false}
                            //     focusedPoi={focusedPoiRef}
                            // />
                            <GMapDeckOverlay
                                showPoi={((selectedGeo.sub_district && !isChecked?.subregionBoundaries) || selectedGeo.village) && cifType.includes('PoI')}
                                showMbp={isChecked?.MBP && myPresence && myPresence?.length > 0 && map && (pathname === RouteConstants.root || pathname === RouteConstants.dashboard)}
                                poi={highlightedPoIs}
                                mbp={myPresence}
                                focusedItem={focusedPoi}
                                onHoverPoi={(type, poi) => handlePopupHover(type, poi, focusedPoi)}
                                handleClick={(type, data) => handlePoiClick(type, data)}
                                brand={hoveredBrand}
                                sub_district={selectedGeo.village || selectedGeo.sub_district}
                                key={selectedGeo.village || selectedGeo.sub_district}
                                otherMarkers={otherMarkers}
                            />
                        )}

                        {/* highlighted poi popup */}
                        {hoveredBrand && pathname === RouteConstants.root &&
                            <AdvancedMarker
                                key={hoveredBrand?.branchId}
                                position={{
                                    lat: hoveredBrand?.latitude,
                                    lng: hoveredBrand?.longitude
                                }}
                                zIndex={200}
                            >
                                <BrandPopup
                                    brand={hoveredBrand}
                                    selBrands={selBrands}
                                    focusedPoi={focusedPoi}
                                    // handleClick={handlePoiClick}
                                    // handleMouseLeave={handlePoiMouseLeave}
                                    handleClose={handlePoiClose}
                                    roi={poiBoundRef.current || null}
                                    handleNavigation={()=>{}}

                                />
                            </AdvancedMarker>
                        }

                        {/* focused brand */}
                        {/* {focusedPoi && from === 'home' &&
                            <AdvancedMarker
                                key={focusedPoi?.branchId}
                                position={{
                                    lat: focusedPoi?.latitude,
                                    lng: focusedPoi?.longitude
                                }}
                                zIndex={200}
                            >
                                <BrandPopup
                                    brand={focusedPoi}
                                    selBrands={selBrands}
                                />
                            </AdvancedMarker>
                        } */}

                        {/* stories pin */}
                        {(pathname === RouteConstants.stories || pathname === RouteConstants.story_details) && uniqueStories && uniqueStories?.map((marker: any) => (
                            <AdvancedMarker
                                key={marker?.familyId}
                                position={{
                                    lng: marker.geometry.coordinates[0],
                                    lat: marker.geometry.coordinates[1]
                                }}
                            >
                                {(pathname === RouteConstants.story_details &&
                                    JSON.stringify(selectedStory?.geometry.coordinates) === JSON.stringify(marker?.geometry.coordinates)) ? (
                                    <img src={highlightedPin} alt="highlighted pin" />
                                ) : (
                                    <img src={pin} alt="pin" />
                                )}
                            </AdvancedMarker>
                        ))}
                    </Map>}

                {showHelperText &&
                    <div className="map-cif-type-warning padding-left-right-5 padding-top-bottom-3 rounded">
                        <Heading
                            title='You can choose only one between PoI and CTV as your base visualisation'
                            type={TypographyType.h5}
                            colour={TypographyColor.white}
                        />
                        <span
                            className="material-symbols-outlined color-white cursor-pointer"
                            onClick={() => setShowHelperText && setShowHelperText(false)}
                        >
                            close
                        </span>
                    </div>
                }
                {pathname === RouteConstants.root && <Button
                    theme={ButtonTheme.secondary}
                    variant={ButtonVariant.bordered}
                    type='button'
                    classname='show-table-view-btn'
                    onClick={() => setOpen(!open)}
                >
                    <span className="d-flex flex-row align-items-center">
                        Show Table View {<LuArrowUpRight />}
                    </span>
                </Button>}

                {/***** Categorical Mode Toast *****/}
                {/* {showCategoricalToast && (
                    <div style={{
                        position: 'absolute', top: 20, right: 20, zIndex: 10000,
                        background: '#fff', borderRadius: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        padding: '10px 14px',
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        maxWidth: 280,
                        animation: 'slideIn 0.3s ease-out',
                    }}>
                        <span className="material-symbols-outlined text-muted-foreground" style={{ fontSize: 18, marginTop: 1, flexShrink: 0 }}>info</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', lineHeight: 1.3 }}>Categorical Choropleth Mode Activated</span>
                            <span style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>Numerical layers are now disabled since they cannot be viewed in Categorical mode.</span>
                        </div>
                        <span
                            className="material-symbols-outlined"
                            style={{ fontSize: 16, color: '#9ca3af', cursor: 'pointer', flexShrink: 0, marginTop: 1 }}
                            onClick={() => setShowCategoricalToast(false)}
                        >close</span>
                    </div>
                )} */}

            </div>

        </>
    )
}


